import clsx from "clsx";
import React from "react";
import { Drawer, DrawerProps, IconButton, Box, Portal } from "@mui/material";

import { ReactComponent as CloseIcon } from "../../icons/Close.svg";
import { ReactComponent as RemoveIcon } from "../../icons/Bin.svg";
import { ReactComponent as ArchiveIcon } from "../../icons/archive.svg";
import {
  ExerciseAssetDto,
  ExerciseAssetStatus,
} from "@growth-machine-llc/stridist-api-client";
import {
  ExerciseEditingType,
  ManageLibraryExercise,
} from "./ManageLibraryExercise";
import { useDeleteLibraryExercise } from "./hooks/useDeleteLibraryExercise";
import { bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import { DeleteArchiveConfirmationTooltipButton } from "../program/modals/DeleteConfirmation.tsx/DeleteConfirmationTooltipButton";
import { exerciseDrawerStyles } from "./exerciseDrawerStyles";
import useArchiveRestoreLibraryExerciseMutation from "./hooks/useArchiveRestoreLibraryExercise";
import { useUserIsClient } from "../../hooks/useCurrentUser";

export interface ExerciseLibraryDrawerProps extends DrawerProps {
  exerciseAsset?: ExerciseAssetDto;
  onSelectExercise?: (exercise: ExerciseAssetDto) => void;
  onUpdate?: (workout: ExerciseAssetDto) => void;
  onCancel?: () => void;
  onRemove?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    exercise?: ExerciseAssetDto,
  ) => void;
  disableChangeExercise?: boolean;
}

//TODO_exercise_library: try to use common drawer component
export function ExerciseLibraryDrawer(props: ExerciseLibraryDrawerProps) {
  const {
    className,
    exerciseAsset,
    onUpdate,
    onCancel,
    onRemove,
    onSelectExercise,
    disableChangeExercise,
    ...other
  } = props;
  const { onClose } = props;
  const s = exerciseDrawerStyles();

  const isClient = useUserIsClient();

  const { mutate: deleteAsset } = useDeleteLibraryExercise(
    exerciseAsset?.isPendingApproval,
    exerciseAsset?.notification,
  );
  const { mutate: archiveAsset } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.PUBLISHED,
  );

  const handleDeleteArchiveClick = React.useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (exerciseAsset?.archived || exerciseAsset?.isPendingApproval) {
        deleteAsset(exerciseAsset.id);
      } else {
        archiveAsset({ id: exerciseAsset.id });
      }
      if (onClose) {
        onClose(event, "backdropClick");
      }
    },
    [exerciseAsset, onClose, deleteAsset],
  );

  const handleClose = React.useCallback(
    (event) => {
      onClose(event, "backdropClick");
    },
    [onClose],
  );

  const deleteArchiveExerciseLibraryConfirmation = usePopupState({
    variant: "popover",
    popupId: "delete-archive-library-exercise-confirmation",
  });

  const deleteExercise = Boolean(
    exerciseAsset?.archived || exerciseAsset?.isPendingApproval,
  );

  return (
    <Portal>
      <Drawer
        className={clsx(s.root, className)}
        classes={{ paper: s.paper }}
        variant="temporary"
        anchor="right"
        {...other}
      >
        <Box className={s.buttons}>
          <Box>
            {exerciseAsset && !isClient && exerciseAsset.coachId && (
              <>
                <DeleteArchiveConfirmationTooltipButton
                  popupState={deleteArchiveExerciseLibraryConfirmation}
                  closeDialog={() => {
                    deleteArchiveExerciseLibraryConfirmation.close();
                  }}
                  confirmAction={(event) => {
                    handleDeleteArchiveClick(event);
                    deleteArchiveExerciseLibraryConfirmation.close();
                  }}
                  anchorOrigin={{ vertical: "center", horizontal: "left" }}
                  transformOrigin={{ vertical: "center", horizontal: "right" }}
                  deleteItem={deleteExercise}
                />
                <IconButton
                  {...bindTrigger(deleteArchiveExerciseLibraryConfirmation)}
                  className={clsx(s.headerButton, s.deleteButton)}
                  size="large"
                >
                  {deleteExercise ? <RemoveIcon /> : <ArchiveIcon />}
                </IconButton>
              </>
            )}
            <IconButton
              className={clsx(s.headerButton, s.closeButton)}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <ManageLibraryExercise
          key={exerciseAsset?.id}
          exerciseAsset={exerciseAsset}
          createNewAsset={!exerciseAsset}
          onSelectExercise={onSelectExercise}
          onClose={handleClose}
          editingType={ExerciseEditingType.ALL}
          enableReadOnlyViewForPublic={true}
          allowChangeExerciseDuringRename={true}
          disableChangeExercise={disableChangeExercise}
          isClient={isClient}
        />
      </Drawer>
    </Portal>
  );
}
